import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Paper, CircularProgress, LinearProgress } from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import CloudIcon from '@mui/icons-material/Cloud';

interface ServiceUnavailableViewProps {
  onRetry: () => void;
}

export const ServiceUnavailableView: React.FC<ServiceUnavailableViewProps> = ({ onRetry }) => {
  const { t } = useLanguage();
  const [autoRetryCountdown, setAutoRetryCountdown] = useState(30);
  const [isAutoRetryEnabled, setIsAutoRetryEnabled] = useState(true);

  useEffect(() => {
    if (!isAutoRetryEnabled || autoRetryCountdown <= 0) return;

    const timer = setInterval(() => {
      setAutoRetryCountdown(prev => {
        if (prev <= 1) {
          onRetry();
          return 30; // Reset countdown
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [autoRetryCountdown, isAutoRetryEnabled, onRetry]);

  const handleManualRetry = () => {
    setAutoRetryCountdown(30);
    onRetry();
  };

  const handleDisableAutoRetry = () => {
    setIsAutoRetryEnabled(false);
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            position: 'relative'
          }}
        >
          <CloudIcon sx={{ fontSize: 40, color: 'white' }} />
          <CircularProgress
            size={90}
            thickness={2}
            sx={{
              position: 'absolute',
              color: 'primary.light',
              animationDuration: '2s'
            }}
          />
        </Box>
        
        <Typography component="h1" variant="h5" gutterBottom align="center" color="primary">
          {t('serviceUnavailable.title')}
        </Typography>
        
        <Typography variant="body1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
          {t('serviceUnavailable.message')}
        </Typography>

        {isAutoRetryEnabled && (
          <Box sx={{ width: '100%', mb: 3 }}>
            <Typography variant="body2" align="center" sx={{ mb: 1 }}>
              {t('serviceUnavailable.autoRetry').replace('{seconds}', autoRetryCountdown.toString())}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={((30 - autoRetryCountdown) / 30) * 100} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleManualRetry}
            fullWidth
          >
            {t('serviceUnavailable.retryButton')}
          </Button>
          
          {isAutoRetryEnabled && (
            <Button
              variant="outlined"
              onClick={handleDisableAutoRetry}
              fullWidth
            >
              {t('serviceUnavailable.disableAutoRetry')}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
}; 